<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="점프 신청"
        subtitle="상담사 목록 상단 노출"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 소속 현장 없음 -->
        <div v-if="!myProperty" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <div>
              <p class="font-semibold text-amber-800">등록된 현장이 없습니다</p>
              <p class="text-sm text-amber-600">현장 등록 후 점프를 신청할 수 있습니다.</p>
            </div>
          </div>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 정보 & 상태 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 내 현장 정보 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-10 h-10 bg-violet-500/10 rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
              </div>
              <div>
                <h3 class="font-bold text-slate-800">{{ myProperty.propertyName }}</h3>
                <p class="text-xs text-slate-500">내 담당 현장</p>
              </div>
            </div>
          </div>

          <!-- 현재 활성 점프 상태 -->
          <div v-if="activeJump" class="bg-violet-50 border border-violet-200 rounded-2xl p-5">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              <span class="font-bold text-violet-800">활성 점프 있음</span>
            </div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-violet-600">현장</span>
                <span class="font-medium text-violet-800">{{ activeJump.propertyName }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-violet-600">시간대</span>
                <span class="font-medium text-violet-800">{{ activeJump.timeSlot?.label }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-violet-600">기간</span>
                <span class="font-medium text-violet-800">{{ formatDateRange(activeJump.startDate, activeJump.endDate) }}</span>
              </div>
            </div>
            <p class="mt-4 text-xs text-violet-600">
              현재 활성 점프가 있어 새로운 점프를 신청할 수 없습니다.
            </p>
          </div>

          <!-- 점프 안내 -->
          <div class="bg-white border border-slate-200 rounded-2xl p-5">
            <h4 class="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              점프 안내
            </h4>
            <ul class="space-y-2 text-sm text-slate-600">
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span><span class="font-semibold text-green-600">무료</span>로 이용 가능</span>
              </li>
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>신청일로부터 <span class="font-semibold">1주일</span> 적용</span>
              </li>
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span><span class="font-semibold">1개 시간대</span>만 선택 가능</span>
              </li>
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>새벽 시간대: 1시간 단위</span>
              </li>
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>그 외 시간대: 30분 단위</span>
              </li>
              <li class="flex items-start gap-2">
                <svg class="w-4 h-4 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>광고 유입 콜은 <strong class="text-amber-600">건당 10,000P</strong>에 구매</span>
              </li>
            </ul>
          </div>

          <!-- 바로가기 -->
          <div class="bg-white border border-slate-200 rounded-2xl p-4">
            <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
            <div class="space-y-2">
              <router-link
                to="/partners/jump/my"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-slate-700">내 점프 관리</span>
                </div>
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
              <router-link
                to="/partners/jump/stats"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-slate-700">점프 효과 통계</span>
                </div>
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
              <router-link
                to="/partners/leads/banner"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-slate-700">광고 유입 콜</span>
                </div>
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
            </div>
          </div>
        </div>

          <!-- 오른쪽: 시간대 선택 -->
          <div class="lg:col-span-2">
            <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div class="p-5 border-b border-slate-100">
              <h2 class="text-lg font-bold text-slate-800">시간대 선택</h2>
              <p class="text-sm text-slate-500 mt-1">원하는 시간대 1개를 선택하세요</p>
            </div>

            <div v-if="activeJump" class="p-5 bg-slate-50 text-center">
              <p class="text-slate-600">활성 점프가 있어 새로운 시간대를 선택할 수 없습니다.</p>
            </div>

            <div v-else class="p-5 max-h-[600px] overflow-y-auto">
              <div v-for="(group, groupIdx) in timeSlotGroups" :key="groupIdx" class="mb-6 last:mb-0">
                <!-- 그룹 헤더 -->
                <div class="flex items-center gap-2 mb-3">
                  <div :class="[
                    'w-8 h-8 rounded-lg flex items-center justify-center',
                    groupColors[group.color]
                  ]">
                    <svg v-if="group.icon === 'moon'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
                    </svg>
                    <svg v-else-if="group.icon === 'sunrise'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <svg v-else-if="group.icon === 'sun'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <span class="font-semibold text-slate-700">{{ group.label }}</span>
                </div>

                <!-- 슬롯 버튼들 -->
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  <button
                    v-for="slot in group.slots"
                    :key="slot.id"
                    @click="selectSlot(slot)"
                    :disabled="isSlotBookedByOther(slot.id)"
                    :class="[
                      'p-3 rounded-xl border-2 transition text-left',
                      selectedSlot?.id === slot.id
                        ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-500/20'
                        : isSlotBookedByOther(slot.id)
                          ? 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed'
                          : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50'
                    ]"
                  >
                    <p :class="[
                      'text-sm font-medium',
                      selectedSlot?.id === slot.id ? 'text-violet-700' : isSlotBookedByOther(slot.id) ? 'text-slate-400' : 'text-slate-700'
                    ]">
                      {{ slot.start }} ~ {{ slot.end }}
                    </p>
                    <p :class="[
                      'text-xs mt-0.5',
                      selectedSlot?.id === slot.id ? 'text-violet-500' : 'text-slate-400'
                    ]">
                      {{ slot.duration }}분
                    </p>
                    <p v-if="isSlotBookedByOther(slot.id)" class="text-xs text-red-400 mt-1">
                      예약됨
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <!-- 선택 완료 & 신청 버튼 -->
            <div v-if="!activeJump" class="p-5 border-t border-slate-100 bg-slate-50">
              <div v-if="selectedSlot" class="mb-4 p-4 bg-white rounded-xl">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm text-slate-500">선택한 시간대</p>
                    <p class="font-bold text-violet-700">{{ selectedSlot.label }}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm text-slate-500">적용 기간</p>
                    <p class="font-medium text-slate-700">{{ formatDateRange(todayDate, endDate) }}</p>
                  </div>
                </div>
              </div>

              <button
                @click="handleApply"
                :disabled="!selectedSlot || applying"
                class="w-full py-4 bg-violet-600 text-white font-semibold rounded-xl hover:bg-violet-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ applying ? '신청 중...' : '점프 신청하기 (무료)' }}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 성공 모달 -->
    <div v-if="showSuccessModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full">
        <div class="text-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-slate-800 mb-2">점프 신청 완료!</h3>
          <p class="text-sm text-slate-600 mb-4">
            {{ appliedResult?.startDate }} ~ {{ appliedResult?.endDate }}<br>
            1주일간 선택한 시간대에 상단 노출됩니다.
          </p>
          <button
            @click="goToMyJumps"
            class="w-full py-3 bg-violet-600 text-white font-semibold rounded-xl hover:bg-violet-700 transition"
          >
            내 점프 확인하기
          </button>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { useJump } from '@/composables/partners/useJump'
import { useAuth } from '@/composables/common/useAuth'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export default {
  name: 'JumpPurchaseView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const router = useRouter()
    const { user } = useAuth()
    const {
      activeJump,
      getTimeSlotGroups,
      checkActiveJump,
      getBookedSlots,
      applyJump,
      getKoreanTime,
      getOneWeekLater
    } = useJump()

    const myProperty = ref(null)
    const selectedSlot = ref(null)
    const bookedSlots = ref([])
    const applying = ref(false)
    const showSuccessModal = ref(false)
    const appliedResult = ref(null)

    // 한국 시간 기준 오늘, 1주일 후
    const todayDate = computed(() => getKoreanTime().date)
    const endDate = computed(() => getOneWeekLater(todayDate.value))

    const timeSlotGroups = computed(() => getTimeSlotGroups())

    const groupColors = {
      slate: 'bg-slate-100 text-slate-600',
      amber: 'bg-amber-100 text-amber-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      orange: 'bg-orange-100 text-orange-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      indigo: 'bg-indigo-100 text-indigo-600'
    }

    // 내 담당 현장 로드
    const loadMyProperty = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'propertyPartners'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          myProperty.value = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          }

          // 예약된 슬롯 로드
          await loadBookedSlots()
        }
      } catch (err) {
        console.error('현장 로드 실패:', err)
      }
    }

    // 예약된 슬롯 로드
    const loadBookedSlots = async () => {
      if (!myProperty.value) return

      const booked = await getBookedSlots(
        myProperty.value.propertyId,
        todayDate.value,
        endDate.value
      )
      bookedSlots.value = booked
    }

    const isSlotBookedByOther = (slotId) => {
      const booked = bookedSlots.value.find(b => b.slotId === slotId)
      // 본인이 예약한 것은 disabled 표시 안함 (하지만 activeJump가 있으면 전체가 disabled됨)
      return booked && booked.partnerId !== user.value?.uid
    }

    const selectSlot = (slot) => {
      if (isSlotBookedByOther(slot.id)) return
      selectedSlot.value = slot
    }

    const formatDateRange = (start, end) => {
      if (!start || !end) return ''
      const formatDate = (d) => {
        const [, m, day] = d.split('-')
        return `${m}/${day}`
      }
      return `${formatDate(start)} ~ ${formatDate(end)}`
    }

    const handleApply = async () => {
      if (!selectedSlot.value || applying.value) return

      if (!confirm('선택한 시간대로 점프를 신청하시겠습니까?')) return

      applying.value = true

      const result = await applyJump(
        myProperty.value.propertyId,
        myProperty.value.propertyName,
        selectedSlot.value.id,
        myProperty.value.displayName
      )

      applying.value = false

      if (result.success) {
        appliedResult.value = result
        showSuccessModal.value = true
      } else {
        alert('신청 실패: ' + result.error)
      }
    }

    const goToMyJumps = () => {
      router.push('/partners/jump/my')
    }

    onMounted(async () => {
      await checkActiveJump()
      await loadMyProperty()
    })

    return {
      myProperty,
      activeJump,
      selectedSlot,
      bookedSlots,
      timeSlotGroups,
      groupColors,
      todayDate,
      endDate,
      applying,
      showSuccessModal,
      appliedResult,
      isSlotBookedByOther,
      selectSlot,
      formatDateRange,
      handleApply,
      goToMyJumps
    }
  }
}
</script>
